// QueueV2Profiles — what staff see after logging a TV in.
//
// Display settings live in the dashboard (Settings -> TV Configuration), so
// this screen does not configure anything: it lists the hospital's profiles
// and starts the display with the one that's picked. Setting five toggles and
// a doctor list on a wall-mounted TV with a remote is the thing we're removing.
import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Logo, Logout } from "../../assets/icons/Icons";
import { useHospDocData } from "../../lib/contexts/HospitalDoctorContext";
import { useUserData } from "../../lib/contexts/UserContext";
import { setAudioLanguageToLocalStorage } from "../../lib/utils/speechUtils";
import { formatDoctorName } from "../../lib/utils/formatDoctorName";
import { TVProfile, fetchProfiles } from "../api/profiles";
import { prefetchHospitalAdVideos } from "../utils/adCache";
import {
  DEFAULT_SCREEN_PREFS,
  loadScreenPrefs,
  saveProfileId,
  saveScreenPrefs,
} from "../utils/selection";

const LANGUAGE_NAMES: Record<string, string> = {
  en: "English",
  kn: "Kannada",
  hi: "Hindi",
  ta: "Tamil",
  te: "Telugu",
  mr: "Marathi",
  ar: "Arabic",
};

const FIELD_LABELS: Array<[keyof TVProfile["fields"], string]> = [
  ["token", "Token"],
  ["patient_name", "Name"],
  ["room_number", "Room"],
  ["slot", "Slot"],
  ["booking_mode", "Booking type"],
];

const QueueV2Profiles: React.FC = () => {
  const { hospital_id } = useParams();
  const navigate = useNavigate();
  const { allDoctorsData, setHospitalID } = useHospDocData();
  const { handleLogout, accessToken } = useUserData();

  const [profiles, setProfiles] = useState<TVProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [starting, setStarting] = useState<string | null>(null);
  const [intervalSec, setIntervalSec] = useState(DEFAULT_SCREEN_PREFS.intervalSec);
  const [ads, setAds] = useState(DEFAULT_SCREEN_PREFS.ads);

  useEffect(() => {
    if (hospital_id) setHospitalID(hospital_id);
  }, [hospital_id, setHospitalID]);

  // Rehydrate this screen's own settings so a rebooted TV keeps its cadence.
  useEffect(() => {
    if (!hospital_id) return;
    const prefs = loadScreenPrefs(hospital_id);
    setIntervalSec(prefs.intervalSec);
    setAds(prefs.ads);
  }, [hospital_id]);

  // Waits for the access token on purpose. It lives in React state and is
  // mirrored into the axios interceptor, so a page refresh starts with no token
  // at all — fetching before it lands returns 401 "No token provided." and this
  // screen has no retry, leaving a permanent "Could not load TV profiles." on a
  // TV that is in fact perfectly authenticated. Staying in the loading state
  // until the token arrives is the whole fix; accessToken is in the deps so
  // this re-runs the moment it does.
  const load = useCallback(async () => {
    if (!hospital_id || !accessToken) return;
    setLoading(true);
    setError(null);
    try {
      setProfiles(await fetchProfiles(hospital_id));
    } catch (err: any) {
      console.error("[queue-v2] profile load failed:", err);
      setError(err?.message || "Could not load TV profiles.");
    } finally {
      setLoading(false);
    }
  }, [hospital_id, accessToken]);

  useEffect(() => {
    load();
  }, [load]);

  // Start downloading this hospital's ad videos into the TV's storage now,
  // while staff are still picking a profile, so the display's first ad plays
  // locally instead of streaming. Same token gate as `load` above.
  useEffect(() => {
    if (!hospital_id || !accessToken) return;
    prefetchHospitalAdVideos(hospital_id);
  }, [hospital_id, accessToken]);

  // Who this profile actually puts on screen. A default profile can't hold
  // doctors (the backend rejects mappings on it), so it means "everyone"
  // rather than "no one". Resolving against allDoctorsData is also what drops
  // hidden and unmapped doctors, so the count matches what staff will see.
  const doctorsFor = (p: TVProfile): string[] => {
    const visible = (allDoctorsData ?? []).filter((d) => d && !d.hide_from_tv);
    // "Dr." unless the doctor opts out, same rule the queue screens use.
    const withPrefix = (name?: string, showPrefix?: boolean) =>
      formatDoctorName(name, showPrefix !== false);
    if (p.isDefault || p.doctors.length === 0) {
      return visible.map((d) => withPrefix(d.full_name, d.show_name_prefix)).filter(Boolean);
    }
    const byId = new Map(visible.map((d) => [d.mapping_id, d]));
    return p.doctors
      .map((d) => {
        const known = byId.get(d.mapping_id);
        // The profiles API doesn't return show_name_prefix, so a doctor we
        // can't resolve locally gets the default (prefix on).
        return known
          ? withPrefix(known.full_name, known.show_name_prefix)
          : withPrefix(d.full_name, true);
      })
      .filter(Boolean);
  };

  // Fullscreen must be requested from inside the click handler — browsers only
  // grant it during a user gesture. It can still be refused, so swallow and
  // navigate either way.
  const goFullscreen = async () => {
    const el = document.documentElement as any;
    const req =
      el.requestFullscreen || el.webkitRequestFullscreen || el.msRequestFullscreen;
    if (!req || document.fullscreenElement) return;
    try {
      await req.call(el);
    } catch {
      /* not permitted — the display still fills the window */
    }
  };

  const start = (profile: TVProfile) => {
    if (!hospital_id) return;
    setStarting(profile.profileId);
    saveProfileId(hospital_id, profile.profileId);
    saveScreenPrefs(hospital_id, { intervalSec, ads });
    // Announcements follow the profile, so a Kannada waiting room and an
    // English one can run off the same hospital.
    setAudioLanguageToLocalStorage(profile.language || "en");
    void goFullscreen();
    navigate(`/${hospital_id}/v2/display`);
  };

  return (
    <div className="min-h-screen w-full bg-[#F7F9FC]">
      <div className="flex items-center justify-between border-b border-[#E9EDF3] bg-white px-8 py-4">
        <div className="flex items-center gap-3">
          <Logo />
          <span className="text-sm font-medium text-[#7A869A]">
            Live Queue · Choose display
          </span>
        </div>
        <button
          onClick={handleLogout}
          className="flex items-center gap-1.5 rounded-full border border-onLeave px-4 py-1.5 text-sm text-onLeave hover:bg-onLeave hover:text-white hover:border-red-400"
        >
          <Logout />
          Logout
        </button>
      </div>

      <div className="mx-auto max-w-5xl px-6 py-10">
        <h1 className="text-3xl font-semibold text-[#1F2B4D]">
          Choose a display profile
        </h1>
        <p className="mt-2 text-[#7A869A]">
          Profiles are set up in the dashboard under Settings → TV
          Configuration. Pick the one this screen should show.
        </p>

        {/* This screen's own settings. Not part of a profile — the same
            profile can run at a different cadence on a lobby TV and a
            corridor TV, and only some screens have an ad panel. */}
        {!loading && !error && profiles.length > 0 && (
          <div className="mt-6 flex flex-wrap items-center gap-6 rounded-xl bg-white p-4 ring-1 ring-[#E9EDF3]">
            <label className="flex items-center gap-3 text-sm text-[#5A6478]">
              Rotate every
              <select
                value={intervalSec}
                onChange={(e) => setIntervalSec(Number(e.target.value))}
                className="rounded-lg border border-[#E3E8F0] bg-white px-3 py-1.5 text-[#1F2B4D]"
              >
                {[5, 8, 10, 15, 20, 30].map((n) => (
                  <option key={n} value={n}>
                    {n}s
                  </option>
                ))}
              </select>
            </label>
            <label className="flex cursor-pointer items-center gap-2 text-sm text-[#5A6478]">
              <input
                type="checkbox"
                checked={ads}
                onChange={(e) => setAds(e.target.checked)}
                className="h-4 w-4 accent-[#0B74FF]"
              />
              Show ads panel
            </label>
            <span className="text-xs text-[#9AA5B8]">
              Applies to this screen. Rotation only kicks in when more doctors
              are selected than fit at once.
            </span>
          </div>
        )}

        {loading ? (
          <div className="mt-16 flex items-center justify-center">
            <div className="h-12 w-12 animate-spin rounded-full border-b-2 border-[#0B74FF]" />
          </div>
        ) : error ? (
          <div className="mt-10 rounded-2xl bg-white p-10 text-center ring-1 ring-[#F6D9D9]">
            <p className="text-[#B23B3B]">{error}</p>
            <button
              onClick={load}
              className="mt-4 rounded-full border border-[#E3E8F0] px-5 py-2 text-sm text-[#5A6478] hover:bg-[#F2F5FA]"
            >
              Try again
            </button>
          </div>
        ) : profiles.length === 0 ? (
          <div className="mt-10 rounded-2xl bg-white p-10 text-center text-[#7A869A] ring-1 ring-[#E9EDF3]">
            <p>No display profiles have been created for this hospital yet.</p>
            <p className="mt-1 text-sm">
              Create one in the dashboard under Settings → TV Configuration.
            </p>
            <button
              onClick={() => navigate(`/${hospital_id}/v2/setup`)}
              className="mt-5 rounded-full bg-[#0B74FF] px-6 py-2.5 text-sm font-semibold text-white hover:bg-[#0a66e0]"
            >
              Set up this screen manually
            </button>
          </div>
        ) : (
          <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2">
            {profiles.map((p) => {
              const names = doctorsFor(p);
              const shown = names.slice(0, 4);
              const rest = names.length - shown.length;
              return (
                <button
                  key={p.profileId}
                  onClick={() => start(p)}
                  disabled={starting !== null}
                  className={`rounded-2xl border bg-white p-5 text-left transition-all ${
                    starting === p.profileId
                      ? "border-[#0B74FF] ring-2 ring-[#0B74FF]/15"
                      : "border-[#E9EDF3] hover:border-[#C9D6EA] hover:shadow-[0_4px_16px_rgba(11,116,255,0.08)]"
                  } ${starting !== null && starting !== p.profileId ? "opacity-50" : ""}`}
                >
                  <div className="flex items-center justify-between">
                    <p className="text-lg font-semibold text-[#1F2B4D]">
                      {p.name}
                    </p>
                    <span className="flex items-center gap-2">
                      {p.isDefault && (
                        <span className="rounded-full bg-[#EAF2FF] px-2.5 py-0.5 text-xs font-medium text-[#0B74FF]">
                          Default
                        </span>
                      )}
                      {/* Its own bordered box, not one of the grey field chips below —
                          those are on/off toggles, this carries a value. Always shown
                          (including "All") so the number sits in the same spot on every
                          card and can be compared down the column at a glance. */}
                      <span className="flex items-baseline gap-1 rounded-lg border border-[#C9D6EA] bg-white px-2.5 py-1 text-[11px] leading-none text-[#7A869A]">
                        Showing
                        <span className="text-sm font-semibold text-[#1F2B4D]">
                          {p.fields.patient_display_limit ?? "All"}
                        </span>
                      </span>
                    </span>
                  </div>

                  <p className="mt-1 text-sm text-[#7A869A]">
                    {p.viewMode === "slideshow" ? "Slideshow view" : "Column view"}
                    {" · "}
                    {names.length} doctor{names.length === 1 ? "" : "s"}
                    {p.isDefault || p.doctors.length === 0 ? " (all)" : ""}
                    {" · "}
                    {LANGUAGE_NAMES[p.language] ?? p.language}
                    {" · rotates every "}
                    {intervalSec}s
                    {ads ? " · with ads" : ""}
                  </p>

                  {/* Who's actually on screen — the thing staff check before
                      walking away from the TV. */}
                  <div className="mt-3 flex flex-wrap gap-1.5">
                    {names.length === 0 ? (
                      <span className="text-xs text-[#B23B3B]">
                        No doctors available — nothing would be shown.
                      </span>
                    ) : (
                      <>
                        {shown.map((name) => (
                          <span
                            key={name}
                            className="rounded-full bg-[#EAF2FF] px-2.5 py-1 text-xs font-medium text-[#0B74FF]"
                          >
                            {name}
                          </span>
                        ))}
                        {rest > 0 && (
                          <span className="rounded-full px-2.5 py-1 text-xs text-[#7A869A]">
                            +{rest} more
                          </span>
                        )}
                      </>
                    )}
                  </div>

                  <div className="mt-3 flex flex-wrap gap-1.5 border-t border-[#F0F3F8] pt-3">
                    {FIELD_LABELS.filter(([key]) => p.fields[key]).map(
                      ([key, label]) => (
                        <span
                          key={key}
                          className="rounded-md bg-[#F2F5FA] px-2 py-1 text-xs text-[#5A6478]"
                        >
                          {label}
                        </span>
                      )
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        )}

        {!loading && !error && profiles.length > 0 && (
          <button
            onClick={() => navigate(`/${hospital_id}/v2/setup`)}
            className="mt-8 rounded-lg border border-[#E3E8F0] bg-white px-5 py-2.5 text-sm font-semibold text-[#1F2B4D] shadow-sm hover:border-[#0B74FF] hover:text-[#0B74FF]"
          >
            Set up this screen manually instead
          </button>
        )}
      </div>
    </div>
  );
};

export default QueueV2Profiles;
